import { useState } from "react";
import useReveal from "../hooks/useReveal";
import { Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { company } from "../mock/mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { createInquiry } from "../lib/api";

export default function Contact() {
  const [ref, inView] = useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact) {
      toast({ title: "Please add your name and an email or phone." });
      return;
    }
    try {
      await createInquiry(form);
      setSent(true);
      toast({ title: "Thank you! We'll be in touch shortly." });
      setForm({ name: "", contact: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast({ title: "Something went wrong. Please call us instead." });
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F2ECE2]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-12 reveal ${inView ? "in-view" : ""}`}>
          <div>
            <p className="text-[#A17E4A] tracking-[0.25em] uppercase text-xs mb-4">Reach Out To Us</p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#2A2521] mb-5">
              Let's Dress Your Windows
            </h2>
            <p className="text-[#6B6259] text-lg mb-8 max-w-md">
              Fill out the form and we'll get back to you shortly to assist with your window
              covering needs — or call us directly for immediate help.
            </p>

            <a
              href={company.phoneHref}
              className="flex items-center gap-4 p-5 rounded-2xl bg-white shadow-sm mb-4 hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-xl bg-[#A17E4A] flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-[#8A8177]">Call us</p>
                <p className="font-serif text-xl text-[#2A2521]">{company.phone}</p>
              </div>
            </a>

            <a
              href={company.mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 p-5 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-xl bg-[#A17E4A] flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-[#8A8177]">Visit us</p>
                <p className="text-[#2A2521]">{company.address}</p>
              </div>
            </a>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg">
            <div className="space-y-5">
              <div>
                <label className="block text-sm text-[#4A423A] mb-2">Your Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="h-12 bg-[#FBF8F3] border-[#E7E0D6] focus-visible:ring-[#A17E4A]"
                />
              </div>
              <div>
                <label className="block text-sm text-[#4A423A] mb-2">Email or Phone</label>
                <Input
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="So we can contact you"
                  className="h-12 bg-[#FBF8F3] border-[#E7E0D6] focus-visible:ring-[#A17E4A]"
                />
              </div>
              <div>
                <label className="block text-sm text-[#4A423A] mb-2">How can we help?</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your windows and style..."
                  rows={4}
                  className="bg-[#FBF8F3] border-[#E7E0D6] focus-visible:ring-[#A17E4A] resize-none"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#A17E4A] hover:bg-[#8a6a3c] text-white rounded-full text-base"
              >
                {sent ? (
                  <><CheckCircle2 className="w-5 h-5 mr-2" /> Sent!</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Submit Inquiry</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
